import React, { createContext, useContext, useState, useMemo, useCallback, useEffect, useRef } from 'react';
import authService from '../services/authService';
import { devLog } from '../utils/devLog';

const SessionContext = createContext();

export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};

export const SessionProvider = ({ children }) => {
  devLog('[SessionProvider] 🚀 SessionProvider 초기화 시작');
  const [expiresIn, setExpiresIn] = useState(null);
  const [isVerifying, setIsVerifying] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authError, setAuthError] = useState(null);
  devLog('[SessionProvider] 📊 초기 상태 - isVerifying:', isVerifying, 'isAuthenticated:', isAuthenticated);

  // ✅ Latest Ref Pattern: 항상 최신 상태 값 참조
  const isVerifyingRef = useRef(isVerifying);
  const isAuthenticatedRef = useRef(isAuthenticated);

  // 상태 변경 시 ref도 동기화
  useEffect(() => {
    isVerifyingRef.current = isVerifying;
  }, [isVerifying]);

  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);

  // 검증 진행 중 플래그 (중복 호출 방지)
  const isVerifyingInProgressRef = useRef(false);

  // 인증 검증 함수 (중앙화, 중복 호출 방지)
  // @param {{ extend?: boolean }} [options] - extend:true 이면 세션 강제 연장
  const verifyAuth = useCallback(async (options = {}) => {
    // 이미 검증 중이면 대기 후 결과 반환
    if (isVerifyingInProgressRef.current) {
      devLog('[SessionContext.verifyAuth] ⚠️ 이미 검증 진행 중 - 중복 호출 방지');
      return { success: false, isDuplicateCall: true };
    }

    devLog('[SessionContext.verifyAuth] 🚀 verifyAuth 시작', options);
    isVerifyingInProgressRef.current = true;
    setIsVerifying(true);
    setAuthError(null);
    devLog('[SessionContext.verifyAuth] 🔄 isVerifying = true 설정');

    const isLoggedIn = authService.isLoggedIn();
    devLog('[SessionContext.verifyAuth] 🔍 isLoggedIn 체크:', isLoggedIn);
    if (!isLoggedIn) {
      devLog('[SessionContext.verifyAuth] ❌ 로그인되지 않음 - 검증 중단');
      setIsAuthenticated(false);
      setIsVerifying(false);
      isVerifyingInProgressRef.current = false;
      devLog('[SessionContext.verifyAuth] ✅ 함수 완료 (로그인 안됨)');
      return { success: false, isLoggedIn: false };
    }

    try {
      devLog('[SessionContext.verifyAuth] ⏳ authService.verifyToken() 호출 시작');
      const result = await authService.verifyToken(options);
      devLog('[SessionContext.verifyAuth] ✅ authService.verifyToken() 완료:', result);
      if (result.success) {
        devLog('[SessionContext.verifyAuth] ✅ 토큰 검증 성공');
        setIsAuthenticated(true);
        devLog('[SessionContext.verifyAuth] 🔄 isAuthenticated = true 설정');
        if (result.expires_in !== undefined) {
          setExpiresIn(result.expires_in);
          devLog('[SessionContext.verifyAuth] ⏰ expiresIn 설정:', result.expires_in);
        }
        devLog('[SessionContext.verifyAuth] ✅ 함수 완료 (성공)');
        return { success: true, expires_in: result.expires_in };
      } else if (!result.isNetworkError) {
        devLog('[SessionContext.verifyAuth] ❌ 토큰 검증 실패 (네트워크 에러 아님)');
        setIsAuthenticated(false);
        setAuthError(result.error);
        devLog('[SessionContext.verifyAuth] 🔄 isAuthenticated = false 설정');
        devLog('[SessionContext.verifyAuth] ✅ 함수 완료 (검증 실패)');
        return { success: false, error: result.error };
      }
      // Network error - keep current state
      devLog('[SessionContext.verifyAuth] 🌐 네트워크 에러 - 상태 유지');
      devLog('[SessionContext.verifyAuth] ✅ 함수 완료 (네트워크 에러)');
      return { success: false, isNetworkError: true };
    } catch (error) {
      console.error('[SessionContext.verifyAuth] ❌ 예외 발생:', error);
      setAuthError(error.message);
      setIsAuthenticated(false);
      devLog('[SessionContext.verifyAuth] ✅ 함수 완료 (예외)');
      return { success: false, error: error.message };
    } finally {
      devLog('[SessionContext.verifyAuth] 🔚 finally 블록 실행');
      setIsVerifying(false);
      isVerifyingInProgressRef.current = false;
      devLog('[SessionContext.verifyAuth] 🔄 isVerifying = false 설정');
    }
  }, []);

  // 인증 준비 대기 함수 (이벤트 기반)
  // ✅ 수정: ref를 통해 최신 상태 참조하여 Stale Closure 문제 해결
  const waitForAuthReady = useCallback(async (maxWaitMs = 5000) => {
    // 이미 인증 완료된 경우 즉시 반환
    if (!isVerifyingRef.current && isAuthenticatedRef.current) {
      return true;
    }

    // 인증 검증 중이면 완료 대기
    return new Promise((resolve) => {
      const startTime = Date.now();
      const checkInterval = setInterval(() => {
        // ✅ ref.current로 항상 최신 값 참조
        if (!isVerifyingRef.current) {
          clearInterval(checkInterval);
          resolve(isAuthenticatedRef.current);
          return;
        }
        // 타임아웃 확인
        if (Date.now() - startTime >= maxWaitMs) {
          clearInterval(checkInterval);
          resolve(false);
        }
      }, 100);
    });
  }, []); // ✅ 의존성 배열 비어있음 - ref는 의존성 불필요

  // 초기 인증 검증 (마운트 시 한 번만)
  useEffect(() => {
    devLog('[SessionProvider] 🔄 초기 인증 검증 useEffect 실행');
    devLog('[SessionProvider] 📞 verifyAuth() 호출');
    verifyAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 빈 배열: 마운트 시 한 번만 실행

  // 주기적 토큰 검증 (1분마다)
  useEffect(() => {
    const interval = setInterval(() => {
      // 로그인 상태이고, 페이지가 활성화되어 있고, 검증 중이 아닐 때만 실행
      if (authService.isLoggedIn() && !document.hidden && !isVerifyingInProgressRef.current) {
        devLog('[SessionProvider] ⏰ 주기적 토큰 검증 실행');
        verifyAuth();
      }
    }, 60000); // 1분 = 60000ms
    
    return () => {
      devLog('[SessionProvider] 🧹 주기적 검증 interval 정리');
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 빈 배열: 마운트 시 한 번만 interval 설정

  // Memoize context value to prevent unnecessary re-renders
  // ✅ expiresIn은 초기값만 전달, 카운트다운은 컴포넌트 로컬에서 관리
  const contextValue = useMemo(
    () => ({
      expiresIn,
      setExpiresIn,
      isVerifying,
      isAuthenticated,
      authError,
      verifyAuth,
      waitForAuthReady
    }),
    [expiresIn, isVerifying, isAuthenticated, authError, verifyAuth, waitForAuthReady]
  );

  return (
    <SessionContext.Provider value={contextValue}>
      {children}
    </SessionContext.Provider>
  );
};

